import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  User,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

export interface UserProfile {
  email: string;
  displayName: string;
  role: 'user' | 'admin';
  createdAt: Date;
  lastLogin: Date;
  isActive: boolean;
}

// Check if this is the first user (should become admin)
const isFirstUser = async (): Promise<boolean> => {
  try {
    const systemConfigRef = doc(db, 'systemConfig', 'settings');
    const configSnap = await getDoc(systemConfigRef);

    // If systemConfig doesn't exist or firstAdminCreated is false, this is the first user
    if (!configSnap.exists()) {
      return true;
    }

    return !configSnap.data()?.firstAdminCreated;
  } catch (error) {
    // If we can't read systemConfig (unauthenticated), assume this is NOT the first user
    // This prevents security issues
    console.error('Error checking first user:', error);
    return false;
  }
};

// Create user profile in Firestore
const createUserProfile = async (user: User, isFirst: boolean) => {
  const userProfileRef = doc(db, 'users', user.uid, 'profile', 'data');

  const profile: UserProfile = {
    email: user.email || '',
    displayName: user.displayName || user.email?.split('@')[0] || 'User',
    role: isFirst ? 'admin' : 'user',
    createdAt: new Date(),
    lastLogin: new Date(),
    isActive: true,
  };

  await setDoc(userProfileRef, profile);

  // If this is the first user (admin), mark it in systemConfig
  if (isFirst) {
    const systemConfigRef = doc(db, 'systemConfig', 'settings');
    await setDoc(systemConfigRef, {
      firstAdminCreated: true,
      firstAdminEmail: user.email,
      createdAt: new Date(),
    });
  }

  return profile;
};

// Get user profile
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  const userProfileRef = doc(db, 'users', userId, 'profile', 'data');
  const profileSnap = await getDoc(userProfileRef);

  if (profileSnap.exists()) {
    return profileSnap.data() as UserProfile;
  }

  return null;
};

// Update last login
const updateLastLogin = async (userId: string) => {
  const userProfileRef = doc(db, 'users', userId, 'profile', 'data');
  await setDoc(userProfileRef, { lastLogin: new Date() }, { merge: true });
};

// Sign up with email and password
export const signUp = async (email: string, password: string) => {
  const isFirst = await isFirstUser();
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const profile = await createUserProfile(userCredential.user, isFirst);

  return { user: userCredential.user, profile };
};

// Sign in with email and password
export const signIn = async (email: string, password: string) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  // Check if profile exists, create if not
  let profile = await getUserProfile(userCredential.user.uid);
  if (!profile) {
    const isFirst = await isFirstUser();
    profile = await createUserProfile(userCredential.user, isFirst);
  } else {
    // Update last login
    await updateLastLogin(userCredential.user.uid);
  }

  return { user: userCredential.user, profile };
};

// Sign in with Google
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  const userCredential = await signInWithPopup(auth, provider);

  // Check if profile exists, create if not
  let profile = await getUserProfile(userCredential.user.uid);
  if (!profile) {
    const isFirst = await isFirstUser();
    profile = await createUserProfile(userCredential.user, isFirst);
  } else {
    // Update last login
    await updateLastLogin(userCredential.user.uid);
  }

  return { user: userCredential.user, profile };
};

// Sign out
export const logout = async () => {
  await signOut(auth);
};
